/**
 * clasificadores/estrategiaFull.js — Clasificador puro del dashboard
 * Estrategia Full (réplica del modelo: publicaciones/catalogo/matriz).
 *
 * ESPEJO de los memos del dashboard (DescargaFullDashboard.jsx): candidatos,
 * lentos (FIFO 60 días de gracia) y reposición (R_total). Si modificás reglas
 * allá, actualizalas también acá.
 *
 * 5 secciones (decisión Mariano 12-jul):
 *   enviar_full       → candidatos: activos, fuera de Full, con ventas en la ventana.
 *                       Consolidado por MLAU (lib pura). EXCLUYE rotación baja
 *                       (<3 u/30d — mandarlos a Full crea sobrestock; declarado).
 *                       Orden: facturación de la ventana DESC.
 *   reponer_full      → en Full, sin stock o va a quebrar antes de que llegue
 *                       la reposición (diasHastaQuiebre < R_total). Trae
 *                       pedirMin / pedirOk. Orden: urgencia + días a quiebre ASC.
 *   La ESCALERA de lentos (ya_paga / va_a_pagar storage) — una sola acción
 *   por item, de menor a mayor resignación:
 *   retirar_full      → ya quemó el ciclo: edad del lote ≥ cicloRetiroDias
 *                       (default 45 de los 60 de gracia) Y la proyección FIFO
 *                       dice que queda stock pagando storage. También si ya
 *                       tiene ad + promo activos y sigue lento (sin palancas).
 *                       Orden: costo proyectado DESC (la plata que perdés primero).
 *   publicitar_lento  → lento SIN tráfico (visitas < piso) y sin ad → falta
 *                       visibilidad (→ activar_ad). Orden: stock en riesgo DESC.
 *   promocionar_lento → lento CON tráfico, sin promo activa y CON espacio de
 *                       rentabilidad (gate ADR 032) → falta incentivo
 *                       (→ activar_promo). Sin costo/corte cargados se sugiere
 *                       con advertencia; sin espacio NO se sugiere (declarado).
 *                       Orden: stock en riesgo DESC.
 *
 * Config empresa (empresa.fullConfig): diasEnvioFull (7), diasProcesamientoFull
 * (3), cicloRetiroDias (45), pisoVisitas (100).
 *
 * Input: { publicaciones (legacy con _fullStock/facetas/_visitas30d), ventas30d,
 *          adsItems, mlPublicacionesVisitas, productos, catalogoLinks,
 *          categorias, marcas, proveedores, productoSourcing,
 *          rentabilidadEmpresa ({rentabilidadMinima} | null),
 *          catalogoFull (tarifas vigentes | null), config (doc empresa),
 *          ventanaVentasDias (días con los que se cargó `ventas30d`) }
 *
 * LA VENTANA (report djynjlXICuUF0Yl803M8): la velocidad de las tres partes
 * divide por `ventanaVentasDias`, no por una constante. Las metas en días
 * (cobertura del envío, rotación baja) son de producto y se normalizan a
 * DIAS_METAS_FULL. Sin ventana (decisión de Mariano del 11-oct-2026, pregunta
 * 29: «deja de calcular y avisa»): enviar_full y la escalera de lentos no se
 * clasifican (las dos necesitan velocidad; aun `ya_paga` la usa para lo que
 * queda al día 60 y el costo), reponer_full sólo emite `sin_stock` con las
 * tasas y el pedido en null, lo no clasificado se cuenta por ítem en
 * `excluidos.sinVentana` y se declara en `resultado.lecturas`.
 */

import { consolidarPorUserProduct, coefOrdenesUnidades, esRotacionBaja, UMBRAL_ROTACION_BAJA, unidadesEnDiasMeta } from '@modules/mercadolibre/lib/consolidarPorUserProduct'
import { ventanaDeVentas, FUENTE_VENTANA } from './ventanaDeVentas'
import { calcularCostoFull } from '@core/lib/catalogoFull'
import { textoGate, construirInfoRentabilidadPorItem, gatePromoConCostosReales, metricasAdsPorItem, gateAdsPrender, textoGateAds } from './gatePromoRentabilidad'
import { estructuraCostosML } from '@core/lib/rentabilidadEstimada'

// ─── Constantes (espejo del dashboard) ───
export const GRACIA_DIAS = 60                    // ML no cobra storage los primeros 60 días
export const UMBRAL_VA_A_PAGAR = 0.25            // > 25% del stock al día 60 → riesgo
export const UMBRAL_APRETADO = 0.05
export const PROYECCION_STORAGE_DIAS = 30
export const CICLO_RETIRO_DEFAULT = 45           // decisión Mariano 12-jul
export const PISO_VISITAS_DEFAULT = 100
const DEFAULT_DIAS_ENVIO_FULL = 7
const DEFAULT_DIAS_PROCESAMIENTO_FULL = 3
const DEFAULT_TIEMPO_REPOSICION_FALLBACK = 30
const REPO_MULT_APRETADO = 1.0
const REPO_MULT_OK = 1.5

// ─── Helpers (espejo del dashboard) ───
function estaEnFull(item) {
  const lt = item?.shipping?.logisticType ?? item?.shipping?.logistic_type
  if (lt === 'fulfillment') return true
  const tags = Array.isArray(item?.shipping?.tags) ? item.shipping.tags : []
  if (tags.includes('fulfillment')) return true
  if (item?.inventoryId ?? item?.inventory_id) return true
  return false
}

const itemIdDe = (p) => String(p?.itemId ?? p?.id ?? '')
const esActiva = (p) => p?.status === 'active' || p?.estado === 'activa'

function tienePromoActiva(p) {
  if (p?.facetas?.enPromo === true) return true
  const deals = Array.isArray(p.dealIds) ? p.dealIds : []
  if (deals.length > 0) return true
  const op = Number(p.originalPrice)
  const px = Number(p.price)
  return Number.isFinite(op) && Number.isFinite(px) && op > px
}

function baseItem(p, extra) {
  return {
    id: itemIdDe(p),
    itemId: itemIdDe(p),
    _itemId: itemIdDe(p),
    title: p.title ?? p.titulo ?? null,
    price: p.price ?? null,
    permalink: p.permalink ?? null,
    ...extra,
  }
}

export function clasificarEstrategiaFull({
  publicaciones = [],
  ventas30d = [],
  adsItems = [],
  mlPublicacionesVisitas = [],
  productos = [],
  catalogoLinks = [],
  categorias = [],
  marcas = [],
  proveedores = [],
  productoSourcing = [],
  rentabilidadEmpresa = null,
  catalogoFull = null,
  config = {},
  umbrales = {},
  impuestosEmpresa = null,   // loader _impuestos (config fiscal mergeada)
  ecosistema = null,         // doc conocimientoEcosistema/costos-ml (loader)
  // La ventana con la que el llamador cargó `ventas30d` (loader del agente:
  // `datos.ventanaVentasDias`; la vista la pasa con la misma de su query).
  ventanaVentasDias = null,
} = {}) {
  const ventanaDias = ventanaDeVentas(ventanaVentasDias)
  const enVentana = ventanaDias === null ? 'en la ventana' : `en ${ventanaDias}d`
  // Por ÍTEM y no por sección: un ítem en Full con stock pasa por reponer y
  // por lentos, y contarlo en los dos inflaría lo que no se pudo clasificar.
  const sinVentana = new Set()
  const fullCfg = config?.fullConfig || {}
  const diasEnvio = Number.isFinite(Number(fullCfg.diasEnvioFull)) && Number(fullCfg.diasEnvioFull) >= 0 ? Number(fullCfg.diasEnvioFull) : DEFAULT_DIAS_ENVIO_FULL
  const diasProc = Number.isFinite(Number(fullCfg.diasProcesamientoFull)) && Number(fullCfg.diasProcesamientoFull) >= 0 ? Number(fullCfg.diasProcesamientoFull) : DEFAULT_DIAS_PROCESAMIENTO_FULL
  const cicloRetiro = Number(umbrales.cicloRetiroDias || fullCfg.cicloRetiroDias) > 0 ? Number(umbrales.cicloRetiroDias || fullCfg.cicloRetiroDias) : CICLO_RETIRO_DEFAULT
  const pisoVisitas = Number(umbrales.pisoVisitas || fullCfg.pisoVisitas) > 0 ? Number(umbrales.pisoVisitas || fullCfg.pisoVisitas) : PISO_VISITAS_DEFAULT

  // ── Ventas por item (unidades + facturación + órdenes) ──
  const ventaPorItem = new Map()
  for (const v of ventas30d) {
    for (const oi of (v.order_items || [])) {
      const itemId = oi?.item?.id
      if (!itemId) continue
      const qty = Number(oi.quantity || 0)
      const curr = ventaPorItem.get(String(itemId)) || { unidades: 0, facturacion: 0, ordenes: 0 }
      curr.unidades += qty
      curr.facturacion += qty * Number(oi.unit_price || 0)
      curr.ordenes += 1
      ventaPorItem.set(String(itemId), curr)
    }
  }

  // ── Visitas 30d ──
  const visitasPorItem = new Map()
  for (const v of mlPublicacionesVisitas) {
    const itemId = v.itemId || v.id
    if (itemId) visitasPorItem.set(String(itemId), Number(v._visitas30d || 0))
  }
  const visitasDe = (p) => {
    const itemId = itemIdDe(p)
    if (visitasPorItem.has(itemId)) return visitasPorItem.get(itemId)
    return Number(p._visitas30d ?? p?.datosCanal?.visitasRecientes?.last30 ?? 0)
  }

  // ── Ads inscriptos — CUALQUIER estado (mismo criterio que Matriz: un ad
  // pausado también cuenta como "ya tiene publicidad"). ──
  const itemsConAd = new Set()
  for (const ad of adsItems) {
    const itemId = ad.item_id || (ad.id ? String(ad.id).split('_').pop() : null)
    if (itemId) itemsConAd.add(String(itemId))
  }
  const conAdDe = (p) => p?.facetas?.conAd === true || itemsConAd.has(itemIdDe(p))

  // ── Rentabilidad: corte por cascada ADR 032 + costo (gate de promos) ──
  const infoRentPorItem = construirInfoRentabilidadPorItem({
    productos, catalogoLinks, categorias, marcas, proveedores, productoSourcing,
    empresaMin: rentabilidadEmpresa?.rentabilidadMinima ?? null,
  })

  // ── Tiempo de reposición por item (para R_total de reposición) ──
  const prodById = new Map()
  for (const p of productos) prodById.set(p.id, p)
  const repoPorItem = new Map()
  for (const link of catalogoLinks) {
    if (link.plataformaId && link.plataformaId !== 'mercado-libre') continue
    const prod = prodById.get(link.productoId)
    if (!prod) continue
    const dias = Number(prod.tiempoReposicionDias)
    if (Number.isFinite(dias) && dias > 0) repoPorItem.set(String(link.externalItemId), dias)
  }

  const activas = publicaciones.filter(esActiva)

  // ═══ 1. ENVIAR A FULL (candidatos, consolidado por MLAU) ═══
  const candidatosRows = []
  let rotacionBaja = 0
  const categoriasEnFull = new Set()
  for (const it of activas) {
    const catId = it.categoryId ?? it.category_id
    if (estaEnFull(it) && catId) categoriasEnFull.add(catId)
  }
  for (const it of activas) {
    if (estaEnFull(it)) continue
    const itemId = itemIdDe(it)
    const venta = ventaPorItem.get(itemId) || { unidades: 0, facturacion: 0, ordenes: 0 }
    if (venta.unidades <= 0) continue
    // Sin ventana no hay sugerido ni rotación: el candidato no se clasifica.
    if (ventanaDias === null) { sinVentana.add(itemId); continue }
    const sugerido = Math.ceil(unidadesEnDiasMeta(venta.unidades, ventanaDias))
    const stockLocal = Number((it.availableQuantity ?? it.available_quantity) || 0)
    const catId = it.categoryId ?? it.category_id
    candidatosRows.push({
      ...it,
      itemId,
      _unidades30d: venta.unidades,
      _facturacion30d: venta.facturacion,
      _ordenes30d: venta.ordenes,
      _stockLocal: stockLocal,
      _sugeridoFull: sugerido,
      _gap: Math.max(0, sugerido - stockLocal),
      _velocidad: venta.unidades / ventanaDias,
      _categoriaYaEnFull: !!(catId && categoriasEnFull.has(catId)),
    })
  }
  const consolidadas = consolidarPorUserProduct(candidatosRows, { ventanaDias })
  const enviar_full = []
  for (const r of consolidadas) {
    if (esRotacionBaja(unidadesEnDiasMeta(r._unidades30d, ventanaDias))) { rotacionBaja++; continue }
    enviar_full.push(baseItem(r, {
      _unidades30d: r._unidades30d,
      _facturacion30d: r._facturacion30d,
      _stockLocal: r._stockLocal,
      _sugeridoFull: r._sugeridoFull,
      _gap: r._gap,
      _categoriaYaEnFull: r._categoriaYaEnFull,
      _coefOrdU: coefOrdenesUnidades(r._ordenes30d, r._unidades30d),
      _motivo: `${r._unidades30d} uds ${enVentana} ($${Math.round(r._facturacion30d).toLocaleString('es-AR')}) fuera de Full — 🎯 ENVIAR ~${r._sugeridoFull} uds (cobertura 30d) · stock local ${r._stockLocal}${r._gap > 0 ? ` (faltan ${r._gap})` : ''}${r._categoriaYaEnFull ? ' · su categoría ya opera en Full' : ''}`,
      metricas: {
        unidades30d: r._unidades30d,
        facturacion30d: r._facturacion30d,
        stockLocal: r._stockLocal,
        sugeridoFull: r._sugeridoFull,
        gap: r._gap,
        categoriaYaEnFull: r._categoriaYaEnFull,
        mlas: r._mlas || [String(r.itemId)],
      },
    }))
  }
  enviar_full.sort((a, b) => (b._facturacion30d ?? 0) - (a._facturacion30d ?? 0))

  // ═══ 2. REPONER EN FULL + 3-5. ESCALERA DE LENTOS ═══
  const reponer_full = []
  const retirar_full = []
  const publicitar_lento = []
  const promocionar_lento = []
  let sinDataFull = 0
  let apretadoRepo = 0
  let lentosSaludables = 0
  let sinEspacioRentabilidad = 0
  let enEspera = 0
  let publicitarLentoAPerdida = 0
  let adsGastanSinVender = 0
  let publicitarLentoSinEspacio = 0
  const gateBloqueadosPromo = []   // itemIds sin espacio → cancelación retroactiva
  // Gate INFERIDO (15-jul): fórmula completa + gasto real de ads de la pub.
  const estructuraCuenta = estructuraCostosML(ventas30d)
  const metricsAdsMap = metricasAdsPorItem(adsItems)

  for (const it of activas) {
    if (!estaEnFull(it)) continue
    const itemId = itemIdDe(it)
    const fs = it._fullStock
    if (!fs) { sinDataFull++; continue }

    const stockFull = Number(fs.availableQuantity || 0)
    const venta = ventaPorItem.get(itemId) || { unidades: 0, facturacion: 0 }
    // ── Reposición: R_total (no depende de la ventana) ──
    const tiempoRepo = repoPorItem.get(itemId) || DEFAULT_TIEMPO_REPOSICION_FALLBACK
    const Rtotal = tiempoRepo + diasEnvio + diasProc

    // Sin ventana: sólo el quiebre consumado (stock 0 con ventas) se puede
    // afirmar; todo lo demás —va a quebrar, lento— necesita la velocidad.
    if (ventanaDias === null) {
      if (stockFull > 0) { sinVentana.add(itemId); continue }
      if (venta.unidades <= 0) continue
      reponer_full.push(baseItem(it, {
        _estadoRepo: 'sin_stock',
        _stockFull: stockFull,
        _diasHastaQuiebre: null,
        _pedirMin: null,
        _pedirOk: null,
        _motivo: `Sin stock en Full (vendía ${venta.unidades} uds ${enVentana}) — ya quebraste`,
        metricas: {
          estadoRepo: 'sin_stock', stockFull, unidades30d: venta.unidades,
          velocidadDiaria: null, Rtotal, diasHastaQuiebre: null,
          pedirMin: null, pedirOk: null, tieneReposicionConfig: repoPorItem.has(itemId),
        },
      }))
      continue
    }
    const velocidad = venta.unidades / ventanaDias

    // ── Reposición: días hasta quiebre (espejo del dashboard) ──
    const diasHastaQuiebre = velocidad > 0 ? stockFull / velocidad : Infinity
    let estadoRepo
    if (stockFull <= 0) estadoRepo = 'sin_stock'
    else if (diasHastaQuiebre < Rtotal * REPO_MULT_APRETADO) estadoRepo = 'va_a_quebrar'
    else if (diasHastaQuiebre < Rtotal * REPO_MULT_OK) estadoRepo = 'apretado_repo'
    else estadoRepo = 'ok_repo'

    if (estadoRepo === 'sin_stock' || estadoRepo === 'va_a_quebrar') {
      // Sin stock pero sin ventas tampoco → no hay nada que reponer con urgencia.
      if (!(estadoRepo === 'sin_stock' && venta.unidades <= 0)) {
        let pedirMin = 0, pedirOk = 0
        if (velocidad > 0 && Rtotal > 0) {
          const consumoEspera = Math.min(stockFull, velocidad * Rtotal)
          pedirMin = Math.max(0, Math.ceil(velocidad * Rtotal - stockFull + consumoEspera))
          pedirOk = Math.max(0, Math.ceil(velocidad * 2 * Rtotal - stockFull + consumoEspera))
        }
        reponer_full.push(baseItem(it, {
          _estadoRepo: estadoRepo,
          _stockFull: stockFull,
          _diasHastaQuiebre: diasHastaQuiebre,
          _pedirMin: pedirMin,
          _pedirOk: pedirOk,
          _motivo: estadoRepo === 'sin_stock'
            ? `Sin stock en Full (vendía ${venta.unidades} uds ${enVentana}) — ya quebraste`
            : `Quiebra en ${Math.floor(diasHastaQuiebre)} días y la reposición tarda ${Rtotal} (R producto ${tiempoRepo} + envío ${diasEnvio} + procesamiento ${diasProc})`,
          metricas: {
            estadoRepo, stockFull, unidades30d: venta.unidades,
            velocidadDiaria: velocidad, Rtotal, diasHastaQuiebre: Number.isFinite(diasHastaQuiebre) ? Math.round(diasHastaQuiebre * 10) / 10 : null,
            pedirMin, pedirOk, tieneReposicionConfig: repoPorItem.has(itemId),
          },
        }))
      }
    } else if (estadoRepo === 'apretado_repo') { apretadoRepo++ }

    // ── Lentos: proyección FIFO al día 60 (espejo del dashboard) ──
    if (stockFull <= 0) continue
    const lotes = Array.isArray(fs.lotes) ? fs.lotes.slice().sort((a, b) => new Date(a.fechaInbound) - new Date(b.fechaInbound)) : []
    let velocidadDisponible = velocidad
    let unidadesQuedanDia60 = 0
    let tieneLoteAging = false
    for (const l of lotes) {
      const remanente = Number(l.remanenteEstimado || 0)
      const diasEnFull = Number(l.diasEnFull || 0)
      const diasHastaStorage = Math.max(0, GRACIA_DIAS - diasEnFull)
      if (diasEnFull >= GRACIA_DIAS && remanente > 0) {
        unidadesQuedanDia60 += remanente
        tieneLoteAging = true
        continue
      }
      const seVenden = Math.min(remanente, velocidadDisponible * diasHastaStorage)
      velocidadDisponible -= (seVenden / Math.max(1, diasHastaStorage))
      if (velocidadDisponible < 0) velocidadDisponible = 0
      unidadesQuedanDia60 += Math.max(0, remanente - seVenden)
    }
    const pctQueda = stockFull > 0 ? unidadesQuedanDia60 / stockFull : 0
    let estadoLento
    if (tieneLoteAging) estadoLento = 'ya_paga'
    else if (pctQueda > UMBRAL_VA_A_PAGAR) estadoLento = 'va_a_pagar'
    else if (pctQueda > UMBRAL_APRETADO) estadoLento = 'apretado'
    else estadoLento = 'saludable'

    if (estadoLento !== 'ya_paga' && estadoLento !== 'va_a_pagar') { lentosSaludables++; continue }

    const diasMaxEnFull = lotes.length > 0 ? Math.max(...lotes.map(l => Number(l.diasEnFull || 0))) : 0
    const conAd = conAdDe(it)
    const conPromo = tienePromoActiva(it)
    const visitas = visitasDe(it)

    // Costo proyectado de storage (si hay catálogo de tarifas + dimensiones)
    let costoProyectado = 0
    if (catalogoFull && it.shipping?.dimensions && unidadesQuedanDia60 > 0) {
      const calc = calcularCostoFull(it.shipping.dimensions, PROYECCION_STORAGE_DIAS, catalogoFull)
      if (calc && !calc.error) costoProyectado = unidadesQuedanDia60 * (calc.costo || 0)
    }

    const lentoBase = {
      _estadoLento: estadoLento,
      _diasMaxEnFull: diasMaxEnFull,
      _stockFull: stockFull,
      _unidadesQuedanDia60: Math.round(unidadesQuedanDia60),
      _pctQuedaDia60: pctQueda,
      _costoProyectado: costoProyectado,
      _visitas30d: visitas,
      _unidades30d: venta.unidades,
    }
    const metricasLento = {
      estadoLento, diasMaxEnFull, stockFull,
      unidadesQuedanDia60: Math.round(unidadesQuedanDia60),
      pctQuedaDia60: Math.round(pctQueda * 1000) / 1000,
      costoProyectado30d: Math.round(costoProyectado),
      visitas30d: visitas, unidades30d: venta.unidades,
    }

    // ── ESCALERA (12-jul): retirar (deadline) → publicitar → promocionar ──
    const cicloCumplido = diasMaxEnFull >= cicloRetiro && unidadesQuedanDia60 > 0
    const palancasAgotadas = conAd && conPromo
    if (cicloCumplido || palancasAgotadas) {
      const motivoRetiro = cicloCumplido
        ? `Ciclo cumplido: ${diasMaxEnFull} días en Full (≥ ${cicloRetiro}) y quedan ~${Math.round(unidadesQuedanDia60)} uds pagando storage`
        : `Con ad y promo activos y sigue lento (${estadoLento === 'ya_paga' ? 'ya paga storage' : 'va a pagar storage'}) — sin palancas restantes`
      retirar_full.push(baseItem(it, {
        ...lentoBase,
        _motivoRetiro: cicloCumplido ? 'ciclo_cumplido' : 'palancas_agotadas',
        _motivo: motivoRetiro,
        metricas: { ...metricasLento, motivoRetiro: cicloCumplido ? 'ciclo_cumplido' : 'palancas_agotadas' },
      }))
    } else if (visitas < pisoVisitas && !conAd) {
      // GATE DE ADS (15-jul-2026): margen proyectado con ACOS esperado —
      // proyectado < 0 veto; < mínima no se sugiere; sin datos con ⚠.
      const rentAd = infoRentPorItem.get(itemId) || { costoUnit: null, corte: null }
      const gateAd = gateAdsPrender({
        precio: it.price,
        margenReal: Number.isFinite(it?._rentabilidadInferida?.margenPctProm) ? it._rentabilidadInferida.margenPctProm : null,
        costoUnit: rentAd.costoUnit, corte: rentAd.corte,
        categoryId: it?.datosCanal?.categoryId ?? null,
        estructuraCuenta, ecosistema, impuestosEmpresa,
        benchmarksEmpresa: config?._benchmarksML || null,
      })
      if (gateAd.estado === 'veto_perdida') {
        publicitarLentoAPerdida++
      } else if (gateAd.estado === 'sin_espacio') {
        publicitarLentoSinEspacio++
      } else {
        publicitar_lento.push(baseItem(it, {
          ...lentoBase,
          _gateAds: gateAd,
          _motivo: `Lento en Full (${estadoLento === 'ya_paga' ? 'ya paga storage' : 'va a pagar storage'}) con solo ${visitas} visitas/30d y sin publicidad — falta visibilidad · ${textoGateAds(gateAd)}`,
          metricas: { ...metricasLento, gateAds: { estado: gateAd.estado, margenProyectadoPct: gateAd.margenProyectadoPct, acosEsperadoPct: gateAd.acosEsperadoPct } },
        }))
      }
    } else if (visitas >= pisoVisitas && !conPromo) {
      const rent = infoRentPorItem.get(itemId) || { costoUnit: null, corte: null }
      // El gasto real de ads se reparte sobre la facturación de la pub
      // (TACOS); ads gastando sin NINGUNA venta → bloqueada con motivo.
      const gate = gatePromoConCostosReales({
        precio: it.price, rent,
        metricsAds: metricsAdsMap.get(itemId) || null,
        ventasItem: venta,
        categoryId: it?.datosCanal?.categoryId ?? null,
        estructuraCuenta, ecosistema, impuestosEmpresa,
      })
      if (gate.estado === 'sin_espacio') {
        if (gate.motivo === 'ads_gastan_sin_vender') adsGastanSinVender++
        else sinEspacioRentabilidad++
        gateBloqueadosPromo.push(itemId)
      } else {
        promocionar_lento.push(baseItem(it, {
          ...lentoBase,
          _gate: gate,
          _motivo: `Lento en Full con ${visitas.toLocaleString('es-AR')} visitas/30d sin promo — ${textoGate(gate)}`,
          metricas: { ...metricasLento, gate: { estado: gate.estado, margenActualPct: gate.margenActualPct, cortePct: gate.cortePct, descuentoMaxPct: gate.descuentoMaxPct, precioPiso: gate.precioPiso } },
        }))
      }
    } else {
      enEspera++
    }
  }

  reponer_full.sort((a, b) => {
    const orden = { sin_stock: 0, va_a_quebrar: 1 }
    const oa = orden[a._estadoRepo] ?? 9
    const ob = orden[b._estadoRepo] ?? 9
    if (oa !== ob) return oa - ob
    return (a._diasHastaQuiebre ?? Infinity) - (b._diasHastaQuiebre ?? Infinity)
  })
  retirar_full.sort((a, b) => (b._costoProyectado || 0) - (a._costoProyectado || 0) || (b._unidadesQuedanDia60 || 0) - (a._unidadesQuedanDia60 || 0))
  publicitar_lento.sort((a, b) => (b._unidadesQuedanDia60 || 0) - (a._unidadesQuedanDia60 || 0))
  promocionar_lento.sort((a, b) => (b._unidadesQuedanDia60 || 0) - (a._unidadesQuedanDia60 || 0))

  return {
    secciones: { enviar_full, reponer_full, retirar_full, publicitar_lento, promocionar_lento },
    analisis: {
      ventanaDias,   // descriptor que lee el contrato del registry
      kpis: {
        enviarFull: enviar_full.length,
        reponerFull: reponer_full.length,
        retirarFull: retirar_full.length,
        publicitarLento: publicitar_lento.length,
        promocionarLento: promocionar_lento.length,
      },
    },
    excluidos: {
      cicloRetiroDias: cicloRetiro,
      pisoVisitasEfectivo: pisoVisitas,
      umbralRotacionBaja: UMBRAL_ROTACION_BAJA,
      rotacionBaja,
      sinDataFull,
      apretadoRepo,
      lentosSaludables,
      sinEspacioRentabilidad,
      publicitarLentoAPerdida,  // flywheel: con ACOS esperado quedaría a pérdida (veto)
      publicitarLentoSinEspacio, // gate ads 15-jul: proyectado abajo de la mínima
      adsGastanSinVender,       // gate 15-jul: ads gastan sin vender — pausar_ad, no promo
      enEspera,
      sinVentana: sinVentana.size,
    },
    // Ítems bloqueados por el gate inferido → cancelación retroactiva (motor).
    gateBloqueados: { promocionar_lento: gateBloqueadosPromo },
    // El canal clasificador → motor (molde: velocidadConsumo), SÓLO si algo
    // quedó sin clasificar por la ventana.
    ...(sinVentana.size > 0 ? {
      lecturas: {
        fuentes: [FUENTE_VENTANA],
        degradaciones: [{ fuente: FUENTE_VENTANA, motivo: `sin-dato: no se sabe con cuántos días de ventas se cargaron los datos — ${sinVentana.size} publicación(es) sin clasificar (enviar a Full, reponer y lentos)` }],
      },
    } : {}),
  }
}
